import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
    getDashboardPreregistrationAllStats,
    getDashboardPreregistrationBySchool,
    getDashboardPreregistrationRefreshAll,
    getDashboardPreregistrationRefreshBySchool,
    getSchoolSettings,
} from "@backend/PreregistrationDashboardRequests";
import { Roles } from "@constants/Roles";

import "./PreregistrationStats.scss";
import { SelectSchoolModal } from "./SelectSchoolModal/SelectSchoolModal";

import { WebRoutes } from "../../../../../Constants/WebRoutes";
import { displayPercent, formatNumber, getPercent, getPropertyFromObject } from "../../../../../Helpers/utils/utils";

export function PreregistrationStats({ loading, schoolList, schoolSelected, setSchoolSelected, setLoading }) {
    const [t] = useTranslation();
    const navigate = useNavigate();
    // @ts-ignore
    const userData = useSelector((state) => state.user.credentials);

    /** @type {[PreregistrationDashboardAllSchools, React.Dispatch<PreregistrationDashboardAllSchools>]} */
    const [allSchoolStats, setAllSchoolStats] = useState({
        totalBooks: 0,
        totalStudents: 0,
        totalSchools: 0,
        countShoolsPreRegStatusNotInitiated: 0,
        countShoolsPreRegStatusInProcess: 0,
        countShoolsPreRegStatusDonePendingIddinkValidation: 0,
        countShoolsPreRegStatusValidatedByIddink: 0,
        exitSchoolsBooksTotal: 0,
        exitSchoolsBooksTakeOver: 0,
        exitSchoolsBooksNotTakeOver: 0,
        exitSchoolsBooksPendingValidate: 0,
        glfSchoolsBooksTotal: 0,
        glfSchoolsBooksToReturn: 0,
        glfSchoolsBooksNotToReturn: 0,
        glfSchoolsBooksPendingValidate: 0,
        elfSchoolsBooksTotal: 0,
        elfSchoolsBooksValidated: 0,
        elfSchoolsBooksPendingValidate: 0,
        booksNotToReturn: 0,
        booksToTakeOver: 0,
        schoolTypeCountGlf: 0,
        schoolTypeCountMiniGlf: 0,
        schoolTypeCountElf: 0,
        schoolTypeCountExit: 0,
    });
    /** @type {[PreregistrationDashboardBySchool, React.Dispatch<PreregistrationDashboardBySchool>]} */
    const [schoolStats, setSchoolStats] = useState(
        /** @type {PreregistrationDashboardBySchool} */ ({
            totalBooks: 0,
            totalStudents: 0,
            preRegStatus: 0,
            schoolType: "",
            booksToBeReturned: 0,
            booksNotToBeReturned: 0,
            booksToTakeOver: 0,
            booksNotToTakeOver: 0,
            isExitSchool: false,
        })
    );
    const [preRegStatus, setPreRegStatus] = useState([]);
    const [schoolTypes, setSchoolTypes] = useState([]);
    const [exitSchools, setExitSchools] = useState([]);
    const [elfSchools, setElfSchools] = useState([]);
    const [glfSchools, setGlfSchools] = useState([]);
    const [bookStatus, setBookStatus] = useState({});
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [updated, setUpdated] = useState(false);
    const [deadlines, setDeadlines] = useState([]);
    const [schoolStatsError, setSchoolStatsError] = useState(false);
    const [isSchoolSelected, setIsSchoolSelected] = useState(false);

    const hasGeneralView =
        userData.role === Roles.Admin ||
        userData.role === Roles.IddinkSchoolManager ||
        userData.role === Roles.IddinkUserManager ||
        userData.role === Roles.IddinkEmployee;

    useEffect(() => {
        if (!hasGeneralView) {
            if (schoolList !== null && schoolList.length > 0) {
                setSchoolSelected(schoolList[0]);
                setIsSchoolSelected(true);
            }
        }
    }, []);

    useEffect(() => {
        if (!isSchoolSelected) {
            getAllStats();
        } else {
            getStats();
        }
    }, [schoolSelected]);

    const getAllStats = async () => {
        setLoading(true);
        const resp = await getDashboardPreregistrationAllStats();
        if (resp.success) {
            initAllStats(resp.data);
        } else {
            setSchoolStatsError(true);
        }
        setLoading(false);
    };
    /**
     * @param {PreregistrationDashboardAllSchools} stats
     */
    const initAllStats = (stats) => {
        setAllSchoolStats({
            totalBooks: stats.totalBooks,
            totalStudents: stats.totalStudents,
            totalSchools: stats.totalSchools,
            countShoolsPreRegStatusNotInitiated: stats.countShoolsPreRegStatusNotInitiated,
            countShoolsPreRegStatusInProcess: stats.countShoolsPreRegStatusInProcess,
            countShoolsPreRegStatusDonePendingIddinkValidation:
                stats.countShoolsPreRegStatusDonePendingIddinkValidation,
            countShoolsPreRegStatusValidatedByIddink: stats.countShoolsPreRegStatusValidatedByIddink,
            exitSchoolsBooksTotal: stats.exitSchoolsBooksTotal,
            exitSchoolsBooksTakeOver: stats.exitSchoolsBooksTakeOver,
            exitSchoolsBooksNotTakeOver: stats.exitSchoolsBooksNotTakeOver,
            exitSchoolsBooksPendingValidate: stats.exitSchoolsBooksPendingValidate,
            glfSchoolsBooksTotal: stats.glfSchoolsBooksTotal,
            glfSchoolsBooksToReturn: stats.glfSchoolsBooksToReturn,
            glfSchoolsBooksNotToReturn: stats.glfSchoolsBooksNotToReturn,
            glfSchoolsBooksPendingValidate: stats.glfSchoolsBooksPendingValidate,
            elfSchoolsBooksTotal: stats.elfSchoolsBooksTotal,
            elfSchoolsBooksValidated: stats.elfSchoolsBooksValidated,
            elfSchoolsBooksPendingValidate: stats.elfSchoolsBooksPendingValidate,
            booksNotToReturn: stats.booksNotToReturn,
            booksToTakeOver: stats.booksToTakeOver,
            schoolTypeCountGlf: stats.schoolTypeCountGlf,
            schoolTypeCountMiniGlf: stats.schoolTypeCountMiniGlf,
            schoolTypeCountElf: stats.schoolTypeCountElf,
            schoolTypeCountExit: stats.schoolTypeCountExit,
        });
        setPreRegStatus(parsePreRegStatus(stats));
        setSchoolTypes(parseSchoolTypes(stats));
        setExitSchools(parseExitSchools(stats));
        setElfSchools(parseElfSchools(stats));
        setGlfSchools(parseGlfSchools(stats));
        setBookStatus(parseBookStatus(stats));
        setSchoolStatsError(false);
        setLoading(false);
    };
    const getStats = async () => {
        setLoading(true);
        Promise.all([getSchoolStats(), getSchoolDeadlines()])
            .then(() => {
                setTimeout(() => {
                    setLoading(false);
                }, 500);
            })
            .catch((error) => {
                console.error("Error fetching stats:", error); // Manejo de errores opcional
                setLoading(false);
            });
    };
    const getSchoolDeadlines = async () => {
        const resp = await getSchoolSettings(schoolSelected.id);
        if (resp.success) {
            setDeadlines(parseDeadlines(resp.data));
        } else {
            setSchoolStatsError(true);
        }
    };
    const getSchoolStats = async () => {
        const resp = await getDashboardPreregistrationBySchool(schoolSelected.id);
        if (resp.success) {
            initSchoolStats(resp.data);
        } else {
            setSchoolStatsError(true);
        }
        // setLoading(false);
    };

    /**
     * @param {PreregistrationDashboardBySchool} stats
     */
    const initSchoolStats = (stats) => {
        setSchoolStats({
            totalBooks: stats.totalBooks,
            totalStudents: stats.totalStudents,
            preRegStatus: stats.preRegStatus,
            schoolType: stats.schoolType,
            booksToBeReturned: stats.booksToBeReturned,
            booksNotToBeReturned: stats.booksNotToBeReturned,
            booksToTakeOver: stats.booksToTakeOver,
            booksNotToTakeOver: stats.booksNotToTakeOver,
            isExitSchool: stats.isExitSchool,
        });
        setBookStatus(parseBookStatus(stats));
        setSchoolStatsError(false);
        setLoading(false);
    };
    const updateAllStats = async () => {
        setUpdated(true);
        setLoading(true);
        if (schoolSelected && isSchoolSelected) {
            //console.log("schoolSelected", schoolSelected);
            const resp = await getDashboardPreregistrationRefreshBySchool(getPropertyFromObject(schoolSelected, "id"));
            if (resp.success) {
                initSchoolStats(resp.data);
            } else {
                setSchoolStatsError(true);
            }
        } else {
            getDashboardPreregistrationRefreshAll().then((resp) => {
                if (resp.success) {
                    initAllStats(resp.data);
                } else {
                    setSchoolStatsError(true);
                }
            });
        }
    };

    /**
     * @param {PreregistrationDashboardAllSchools} stats
     */
    const parsePreRegStatus = (stats) => {
        return [
            {
                name: "NotInitiated",
                value: getPercent(stats.countShoolsPreRegStatusNotInitiated, stats.totalSchools),
            },
            {
                name: "InProcess",
                value: getPercent(stats.countShoolsPreRegStatusInProcess, stats.totalSchools),
            },
            {
                name: "PendingIddink",
                value: getPercent(stats.countShoolsPreRegStatusDonePendingIddinkValidation, stats.totalSchools),
            },
            {
                name: "Finished",
                value: getPercent(stats.countShoolsPreRegStatusValidatedByIddink, stats.totalSchools),
            },
        ];
    };
    /**
     * @param {PreregistrationDashboardAllSchools} stats
     */
    const parseSchoolTypes = (stats) => {
        return [
            {
                name: "GLF",
                value: getPercent(stats.schoolTypeCountGlf, stats.totalSchools),
            },
            {
                name: "MiniGLF",
                value: getPercent(stats.schoolTypeCountMiniGlf, stats.totalSchools),
            },
            {
                name: "ELF",
                value: getPercent(stats.schoolTypeCountElf, stats.totalSchools),
            },
            {
                name: "Exit",
                value: getPercent(stats.schoolTypeCountExit, stats.totalSchools),
            },
        ];
    };
    /**
     * @param {PreregistrationDashboardAllSchools} stats
     */
    const parseExitSchools = (stats) => {
        return [
            {
                name: "Total",
                value: stats.exitSchoolsBooksTotal,
                percent: getPercent(stats.exitSchoolsBooksTotal, stats.exitSchoolsBooksTotal),
            },
            {
                name: "Takeover",
                value: stats.exitSchoolsBooksTakeOver,
                percent: getPercent(stats.exitSchoolsBooksTakeOver, stats.exitSchoolsBooksTotal),
            },
            {
                name: "NotTakeover",
                value: stats.exitSchoolsBooksNotTakeOver,
                percent: getPercent(stats.exitSchoolsBooksNotTakeOver, stats.exitSchoolsBooksTotal),
            },
            {
                name: "PendingToValidate",
                value: stats.exitSchoolsBooksPendingValidate,
                percent: getPercent(stats.exitSchoolsBooksPendingValidate, stats.exitSchoolsBooksTotal),
            },
        ];
    };
    /**
     * @param {PreregistrationDashboardAllSchools} stats
     */
    const parseElfSchools = (stats) => {
        return [
            {
                name: "Total",
                value: stats.elfSchoolsBooksTotal,
                percent: getPercent(stats.elfSchoolsBooksTotal, stats.elfSchoolsBooksTotal),
            },
            {
                name: "Confirmed",
                value: stats.elfSchoolsBooksValidated,
                percent: getPercent(stats.elfSchoolsBooksValidated, stats.elfSchoolsBooksTotal),
            },
            {
                name: "Pending",
                value: stats.elfSchoolsBooksPendingValidate,
                percent: getPercent(stats.elfSchoolsBooksPendingValidate, stats.elfSchoolsBooksTotal),
            },
        ];
    };
    /**
     * @param {PreregistrationDashboardAllSchools} stats
     */
    const parseGlfSchools = (stats) => {
        return [
            {
                name: "Total",
                value: stats.glfSchoolsBooksTotal,
                percent: getPercent(stats.glfSchoolsBooksTotal, stats.glfSchoolsBooksTotal),
            },
            {
                name: "Returned",
                value: stats.glfSchoolsBooksToReturn,
                percent: getPercent(stats.glfSchoolsBooksToReturn, stats.glfSchoolsBooksTotal),
            },
            {
                name: "NotReturned",
                value: stats.glfSchoolsBooksNotToReturn,
                percent: getPercent(stats.glfSchoolsBooksNotToReturn, stats.glfSchoolsBooksTotal),
            },
            {
                name: "PendingToValidate",
                value: stats.glfSchoolsBooksPendingValidate,
                percent: getPercent(stats.glfSchoolsBooksPendingValidate, stats.glfSchoolsBooksTotal),
            },
        ];
    };

    const parseBookStatus = (stats) => {
        if (!isSchoolSelected) {
            return {
                percentBooksNotToReturn: getPercent(stats.booksNotToReturn, stats.totalBooks),
                percentBooksToTakeOver: getPercent(stats.booksToTakeOver, stats.totalBooks),
            };
        } else {
            return {
                percentBooksNotToReturn: getPercent(stats.booksNotToBeReturned, stats.totalBooks),
                percentBooksToTakeOver: getPercent(stats.booksToTakeOver, stats.totalBooks),
            };
        }
    };
    const parseDeadlines = (stats) => {
        return [
            {
                name: "SchoolPreRegistration",
                value: stats.preRegSchoolEndDate,
            },
            {
                name: "IddinkPreRegistration",
                value: stats.preRegSchoolAdminEndDate,
            },
            {
                name: "StudentReturn",
                value: stats.preRegStudentEndDate,
            },
            {
                name: "Handover",
                value: stats.returningBooksEndDate,
            },
        ];
    };

    const selectSchool = (school) => {
        setSchoolSelected(school);
        setIsSchoolSelected(true);
    };

    const onClickClear = () => {
        setSchoolSelected(null);
        setIsSchoolSelected(false);
    };

    return (
        <div className="school-stats trans-fromTop">
            <div className="header-wrapper">
                {schoolList.length > 0 && (
                    <>
                        <div className="select-school-wrapper" onClick={() => setShowSelectModal(true)}>
                            <div className="select-school">
                                <div className="school-number">
                                    {isSchoolSelected && schoolSelected ? schoolSelected?.schoolNumber : "-"}
                                </div>
                                <div className="school-name">
                                    {isSchoolSelected && schoolSelected ? schoolSelected?.name : t("SelectSchool")}
                                </div>
                            </div>
                            <div className="icon">compare_arrows</div>
                        </div>
                        {hasGeneralView && isSchoolSelected && (
                            <span className="btn-clear" onClick={() => onClickClear()}>
                                {t("Clear")}
                            </span>
                        )}
                    </>
                )}
                <div className="update-stats-wrapper">
                    {/* <div className="text">
                        {t("LastUpdateDateTime")}: {displayDateTime(parseDateFromUtc(schoolBooksStats.date))}{" "}
                    </div> */}
                    {/* {updated === false && ( */}
                    <div className="update-btn" title={t("UpdateStats")} onClick={() => updateAllStats()}>
                        refresh
                    </div>
                    {/* )} */}
                </div>
            </div>
            {loading ? (
                <div className="loader-container">
                    <div className="loader" />
                </div>
            ) : (
                <div className="chart-wrapper">
                    {schoolStatsError ? (
                        <div className="panel-wrapper-row">
                            <div className="panel error">
                                <div className="icon">warning</div>
                                <div className="text">{t("ErrorLoadingStats")}</div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="column-wrapper">
                                <div className="panel-wrapper-row">
                                    <div
                                        className="panel"
                                        onClick={() => navigate(WebRoutes.PreregistrationBooks)}
                                        style={{ cursor: "pointer" }} // Cambia el cursor para indicar que es clickeable
                                    >
                                        <div className="subtitle">{t("TotalBooks")}</div>
                                        <div className="value">
                                            {formatNumber(
                                                !isSchoolSelected ? allSchoolStats.totalBooks : schoolStats.totalBooks
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {!isSchoolSelected && (
                                    <div className="panel-wrapper-row">
                                        <div
                                            className="panel"
                                            onClick={() => navigate(WebRoutes.PreregistrationSchools)} // Redirige al hacer clic
                                            style={{ cursor: "pointer" }} // Cambia el cursor para indicar que es clickeable
                                        >
                                            <div className="subtitle">{t("TotalSchools")}</div>
                                            <div className="value">{formatNumber(allSchoolStats.totalSchools)}</div>
                                        </div>
                                    </div>
                                )}
                                {isSchoolSelected && (
                                    <div className="panel-wrapper-row">
                                        <div
                                            className="panel"
                                            // onClick={() => navigate(WebRoutes.PreregistrationSchools)} // Redirige al hacer clic
                                            style={{ cursor: "pointer" }} // Cambia el cursor para indicar que es clickeable
                                        >
                                            <div className="subtitle">{t("TotalStudents")}</div>
                                            <div className="value">{formatNumber(schoolStats.totalStudents)}</div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="column-wrapper">
                                {!isSchoolSelected && (
                                    <div className="chart">
                                        <div className="bar-chart">
                                            <div className="subtitle">{t("PreRegistrationStatus")}</div>
                                            {preRegStatus.map((item, index) => (
                                                <div key={index} className="bar-container">
                                                    <div
                                                        className="label pointer"
                                                        // onClick={() => {
                                                        //     const params = {
                                                        //         reportType: getReportTypeIdByName(item.name),
                                                        //         schoolId: schoolSelected.id,
                                                        //     };
                                                        //     navigate(
                                                        //         WebRoutes.StudentOverviewPage +
                                                        //             objectToQueryString(params, true)
                                                        //     );
                                                        // }}
                                                    >
                                                        {t(item.name)}
                                                    </div>
                                                    <div className="bar-wrapper">
                                                        <div className="bar-full">
                                                            <div
                                                                className="bar"
                                                                style={{ width: `${item.value}%` }}
                                                            ></div>
                                                        </div>
                                                        <div className="bar-label">{item.value}%</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {isSchoolSelected && (
                                    <div className="chart-deadlines">
                                        <div className="bar-chart">
                                            <div className="subtitle">{t("SchoolDeadlines")}</div>
                                            {deadlines.map((item, index) => (
                                                <div key={index} className="bar-container-deadlines">
                                                    <div className="label-deadlines">{t(item.name)}</div>
                                                    <div className="bar-label-deadlines">
                                                        {new Date(item.value).toLocaleDateString()}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {isSchoolSelected && (
                                    <div className="panel-wrapper-row">
                                        <div className="panel">
                                            <div className="subtitle">{t("BookStatus")}</div>
                                            <div className="stats-row">
                                                <div className="stats-column not-logged">
                                                    <div className="static-value">
                                                        {formatNumber(allSchoolStats.booksNotToReturn)}
                                                    </div>
                                                    <div className="hover-value">
                                                        {/* @ts-ignore */}
                                                        {displayPercent(bookStatus.percentBooksNotToReturn)}
                                                    </div>
                                                    <div className="text">{t("NotReturned")}</div>
                                                </div>
                                                <div className="stats-column logged">
                                                    <div className="static-value">
                                                        {formatNumber(allSchoolStats.booksToTakeOver)}
                                                    </div>
                                                    <div className="hover-value">
                                                        {/* @ts-ignore */}
                                                        {displayPercent(bookStatus.percentBooksToTakeOver)}
                                                    </div>
                                                    <div className="text">{t("Takeover")}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {!isSchoolSelected && (
                                    <div className="chart">
                                        <div className="bar-chart">
                                            <div className="subtitle">{t("SchoolTypes")}</div>
                                            {schoolTypes.map((item, index) => (
                                                <div key={index} className="bar-container">
                                                    <div
                                                        className="label pointer"
                                                        // onClick={() => {
                                                        //     const params = {
                                                        //         reportType: getReportTypeIdByName(item.name),
                                                        //         schoolId: schoolSelected.id,
                                                        //     };
                                                        //     navigate(
                                                        //         WebRoutes.StudentOverviewPage +
                                                        //             objectToQueryString(params, true)
                                                        //     );
                                                        // }}
                                                    >
                                                        {t(item.name)}
                                                    </div>
                                                    <div className="bar-wrapper">
                                                        <div className="bar-full">
                                                            <div
                                                                className="bar"
                                                                style={{ width: `${item.value}%` }}
                                                            ></div>
                                                        </div>
                                                        <div className="bar-label">{item.value}%</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {!isSchoolSelected && (
                                <div className="column-wrapper">
                                    <div className="panel-wrapper-row">
                                        <div className="panel">
                                            <div className="subtitle">{t("BookStatus")}</div>
                                            <div className="stats-row">
                                                <div className="stats-column not-logged">
                                                    <div className="static-value">
                                                        {formatNumber(allSchoolStats.booksNotToReturn)}
                                                    </div>
                                                    <div className="hover-value">
                                                        {/* @ts-ignore */}
                                                        {displayPercent(bookStatus.percentBooksNotToReturn)}
                                                    </div>
                                                    <div className="text">{t("NotReturned")}</div>
                                                </div>
                                                <div className="stats-column logged">
                                                    <div className="static-value">
                                                        {formatNumber(allSchoolStats.booksToTakeOver)}
                                                    </div>
                                                    <div className="hover-value">
                                                        {/* @ts-ignore */}
                                                        {displayPercent(bookStatus.percentBooksToTakeOver)}
                                                    </div>
                                                    <div className="text">{t("Takeover")}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {!isSchoolSelected && (
                                        <div className="chart">
                                            <div className="bar-chart">
                                                <div className="subtitle">{t("BooksGLFMiniGLF")}</div>
                                                {glfSchools.map((item, index) => (
                                                    <div key={index} className="bar-container">
                                                        <div
                                                            className="label pointer"
                                                            // onClick={() => {
                                                            //     const params = {
                                                            //         reportType: getReportTypeIdByName(item.name),
                                                            //         schoolId: schoolSelected.id,
                                                            //     };
                                                            //     navigate(
                                                            //         WebRoutes.StudentOverviewPage +
                                                            //             objectToQueryString(params, true)
                                                            //     );
                                                            // }}
                                                        >
                                                            {t(item.name)}
                                                        </div>
                                                        <div className="bar-wrapper">
                                                            <div className="bar-full">
                                                                <div
                                                                    className="bar"
                                                                    style={{ width: `${item.percent}%` }}
                                                                ></div>
                                                            </div>
                                                            <div className="bar-label">
                                                                {item.percent}% ({item.value})
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!isSchoolSelected && (
                                <div className="column-wrapper">
                                    <div className="chart">
                                        <div className="bar-chart">
                                            <div className="subtitle">{t("ExitSchools")}</div>
                                            {exitSchools.map((item, index) => (
                                                <div key={index} className="bar-container">
                                                    <div
                                                        className="label pointer"
                                                        // onClick={() => {
                                                        //     const params = {
                                                        //         reportType: getReportTypeIdByName(item.name),
                                                        //         schoolId: schoolSelected.id,
                                                        //     };
                                                        //     navigate(
                                                        //         WebRoutes.StudentOverviewPage +
                                                        //             objectToQueryString(params, true)
                                                        //     );
                                                        // }}
                                                    >
                                                        {t(item.name)}
                                                    </div>
                                                    <div className="bar-wrapper">
                                                        <div className="bar-full">
                                                            <div
                                                                className="bar"
                                                                style={{ width: `${item.percent}%` }}
                                                            ></div>
                                                        </div>
                                                        <div className="bar-label">
                                                            {item.percent}%({item.value})
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="chart">
                                        <div className="bar-chart">
                                            <div className="subtitle">{t("ELF")}</div>
                                            {elfSchools.map((item, index) => (
                                                <div key={index} className="bar-container">
                                                    <div
                                                        className="label pointer"
                                                        // onClick={() => {
                                                        //     const params = {
                                                        //         reportType: getReportTypeIdByName(item.name),
                                                        //         schoolId: schoolSelected.id,
                                                        //     };
                                                        //     navigate(
                                                        //         WebRoutes.StudentOverviewPage +
                                                        //             objectToQueryString(params, true)
                                                        //     );
                                                        // }}
                                                    >
                                                        {t(item.name)}
                                                    </div>
                                                    <div className="bar-wrapper">
                                                        <div className="bar-full">
                                                            <div
                                                                className="bar"
                                                                style={{ width: `${item.percent}%` }}
                                                            ></div>
                                                        </div>
                                                        <div className="bar-label">
                                                            {item.percent}% ({item.value})
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            {showSelectModal && (
                <>
                    <SelectSchoolModal
                        show={showSelectModal}
                        schoolList={schoolList}
                        schoolSelected={isSchoolSelected && schoolSelected ? schoolSelected : {}}
                        setSchoolSelected={selectSchool}
                        onHide={() => setShowSelectModal(false)}
                    />
                </>
            )}
        </div>
    );
}
